import { useMemo } from "react";
import { Card, Empty, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { Line } from "@ant-design/charts";
import { PROGRESSION_CHART_COLORS } from "./chartConstants";
import "./ProgressionLineChart.scss";

const { Text } = Typography;

// Lo que se pinta cuando una medicion no trae total.
const SIN_DATO = "—";

export function ProgressionLineChart({ loading, history }) {
  const { t } = useTranslation("motricity");
  const hist = useMemo(() => history ?? [], [history]);

  const isMultiLine =
    hist.length > 0 && hist[0]?.processedScore?.dimensions?.length > 0;

  const data = useMemo(() => {
    if (hist.length === 0) return [];

    if (isMultiLine) {
      return hist.flatMap((item) =>
        (item.processedScore?.dimensions || []).map((dim) => ({
          date: item.sessionDate.slice(0, 10),
          value: dim.score,
          serie: dim.name,
        })),
      );
    }

    return hist.map((item) => ({
      date: item.sessionDate.slice(0, 10),
      value: item.processedScore?.total ?? 0,
      serie: t("charts.line.seriesTotal"),
      interpretation: item.processedScore?.interpretation || "",
    }));
  }, [hist, isMultiLine, t]);

  /**
   * Delta NEUTRO: dice de cuanto a cuanto ha ido la puntuacion, sin afirmar si
   * eso es bueno o malo.
   *
   * Antes habia una flecha verde hacia arriba y otra roja hacia abajo. Asumian
   * que mas puntuacion es mejor, y en la mitad del catalogo es al reves: en
   * GDS-15, STAI y NPI una puntuacion que sube significa que el paciente esta
   * peor. A un paciente cuya GDS-15 pasaba de 3 a 11 -de sin depresion a
   * depresion establecida- se le pintaba una flecha verde.
   *
   * No se puede arreglar aqui: `reverse` existe a nivel de PREGUNTA, para
   * invertir un item suelto, y no hay ningun campo a nivel de escala que diga
   * si un total alto es bueno o malo. Hasta que el template lo lleve, el
   * indicador se retira: una flecha ausente no informa, una invertida
   * desinforma.
   */
  const deltaNeutro = useMemo(() => {
    if (hist.length < 2) return null;
    const anterior = hist[hist.length - 2].processedScore?.total ?? SIN_DATO;
    const actual = hist[hist.length - 1].processedScore?.total ?? SIN_DATO;
    return (
      <Text type="secondary">
        {t("charts.titular.delta", { anterior, actual })}
      </Text>
    );
  }, [hist, t]);

  const config = useMemo(
    () => ({
      data,
      xField: "date",
      yField: "value",
      seriesField: isMultiLine ? "serie" : undefined,
      colorField: isMultiLine ? "serie" : undefined,
      color: isMultiLine ? PROGRESSION_CHART_COLORS : ["#007BFF"],
      smooth: true,
      autoFit: true,
      height: 280,
      point: { size: 4 },
      yAxis: { min: 0 },
      tooltip: {
        formatter: (datum) => ({
          name: datum.serie || t("charts.line.seriesScore"),
          // La interpretacion es DATO del servidor: entra en la frase sin
          // traducir, pero la frase si es interfaz.
          value: datum.interpretation
            ? t("charts.progression.valorConInterpretacion", {
                valor: datum.value,
                interpretacion: datum.interpretation,
              })
            : String(datum.value),
        }),
      },
    }),
    [data, isMultiLine, t],
  );

  return (
    <Card
      title={t("charts.line.title")}
      extra={deltaNeutro}
      loading={loading}
      variant="outlined"
      className="revi-eval-chart-card revi-progression-line"
    >
      {!loading && hist.length < 2 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            hist.length === 0
              ? t("charts.line.emptyNoData")
              : t("charts.line.emptyNeedTwo")
          }
        />
      ) : (
        <Line {...config} />
      )}
    </Card>
  );
}
